import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Star, ArrowRight, MessageCircle, Phone, Mail } from 'lucide-react';
import TextEffect from '../primitives/TextEffect';
import Pollen2D from '../Pollen2D';
import Mosquito2D from '../Mosquito2D';
import Pill from '../ui/Pill';
import { useLang } from '../../LangContext';
import { mediaAlt } from '../../lib/mediaSeo';

/**
 * HeroBlock — Act I (Promise).
 *
 * Single-viewport "above the fold" hero. Photo fills the whole section
 * and ticks slowly upward as the user scrolls (parallax). Headline +
 * sub + CTAs sit at the bottom over a cream gradient.
 *
 * Layout (desktop):
 *   [ ............. empty (mt-auto) ............. ]
 *   [ H1, full width, bottom-aligned to chevron     ]
 *   [ sub | spar-bundles inline anchor | CTAs       ]
 *   [ chevron-only scroll indicator (bottom-center) ]
 *
 * Parallax: the image gets a `transform: translate3d(0, -y*0.30, 0)`
 * applied with requestAnimationFrame, capped to the section's height
 * to avoid blank bands. The image is intentionally rendered at 120%
 * height with a -10% top offset to provide travel room.
 */
export default function HeroBlock({
  eyebrow, h1, sub, mediaSrc, mediaFallback, ctas = [], scrollHint, bundles = [],
  enableSecretAdminTrigger = false, particleEffect = 'pollen',
}) {
  const { lang, overlay } = useLang();
  const [line1, line2] = h1;
  const heroAlt = mediaAlt(overlay, mediaSrc, lang, eyebrow || line1 || '');
  const primary = ctas.find((c) => c.kind === 'primary');
  const whatsapp = ctas.find((c) => c.kind === 'whatsapp');
  const phone = ctas.find((c) => c.kind === 'phone');
  // Highest discount across all bundles — drives the sticker badge.
  const maxSavePct = bundles.reduce((m, b) => Math.max(m, b.savePct || 0), 0);
  // Hover state for the Spar-Bundles circle — pauses the color-flip
  // loop and gives the button a controlled 120% emphasis.
  const [bundleHover, setBundleHover] = useState(false);
  const mobilePillRef = useRef(null);
  const trustTapRef = useRef({ count: 0, lastAt: 0, timer: null });

  const sectionRef = useRef(null);
  const imgRef = useRef(null);
  // Banner row above the CTAs aligns with WhatsApp.left → Phone.right.
  // Measured at runtime via testIds because the Pill component wraps
  // itself in <Magnet/>, hiding any ref we could pass down.
  const [bannerRect, setBannerRect] = useState(null);
  const desktopBundleSize = bannerRect ? Math.min(Math.max(bannerRect.widthPx, 300), 350) : 0;

  const onScrollHint = () => {
    const target = document.querySelector('[data-testid="bundles-section"]');
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const onSecretTrustTap = () => {
    if (!enableSecretAdminTrigger || typeof window === 'undefined') return;
    const now = Date.now();
    const state = trustTapRef.current;
    if (now - state.lastAt > 2500) state.count = 0;
    state.lastAt = now;
    state.count += 1;
    if (state.timer) window.clearTimeout(state.timer);
    if (state.count >= 5) {
      state.count = 0;
      state.timer = null;
      window.location.assign('https://horrenmaker.nl/admin');
      return;
    }
    state.timer = window.setTimeout(() => {
      state.count = 0;
      state.timer = null;
    }, 2500);
  };

  useEffect(() => {
    const state = trustTapRef.current;
    return () => {
      if (state.timer && typeof window !== 'undefined') window.clearTimeout(state.timer);
    };
  }, []);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const measure = () => {
      if (window.innerWidth < 1024) { setBannerRect(null); return; }
      const wa = document.querySelector('[data-testid="hero-cta-whatsapp"]');
      const ph = document.querySelector('[data-testid="hero-cta-phone"]');
      if (!wa || !ph) return;
      const waRect = wa.getBoundingClientRect();
      const phRect = ph.getBoundingClientRect();
      const next = {
        leftPx: Math.round(waRect.left),  // viewport-relative left
        widthPx: Math.round(phRect.right - waRect.left),
      };
      setBannerRect((prev) =>
        prev && prev.leftPx === next.leftPx && prev.widthPx === next.widthPx ? prev : next
      );
    };
    const raf1 = requestAnimationFrame(() => requestAnimationFrame(measure));
    // Re-measure once fonts/CTAs have settled
    const t1 = setTimeout(measure, 250);
    const t2 = setTimeout(measure, 1000);
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(raf1);
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('resize', measure);
    };
  }, []);

  // Parallax — desktop only, prefers-reduced-motion respected.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || window.innerWidth < 1024) return;

    let raf = null;
    let target = 0;
    let current = 0;

    const tick = () => {
      // ease toward target for buttery feel
      current += (target - current) * 0.12;
      if (imgRef.current) {
        imgRef.current.style.transform = `translate3d(0, ${current.toFixed(2)}px, 0)`;
      }
      if (Math.abs(target - current) > 0.1) {
        raf = requestAnimationFrame(tick);
      } else {
        raf = null;
      }
    };

    const onScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      // Only animate while section overlaps viewport
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      const progress = -rect.top; // 0 at top of page, +N when scrolled past
      // Parallax factor: photo moves up at 0.30× the scroll distance,
      // clamped to a sensible band (image has +20% headroom).
      const cap = sectionRef.current.offsetHeight * 0.18;
      target = Math.max(-cap, Math.min(cap, progress * 0.30));
      if (!raf) raf = requestAnimationFrame(tick);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // sync initial position
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section
      id="top"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative w-full overflow-hidden bg-[#FAF8F2] flex flex-col"
      style={{ minHeight: 'max(640px, 100svh)' }}
    >
      {/* Photo backdrop — rendered at 120% with -10% top so the
          parallax translation never exposes the bg cream. */}
      <img
        ref={imgRef}
        src={mediaSrc}
        onError={(e) => { if (mediaFallback) e.currentTarget.src = mediaFallback; }}
        alt={heroAlt}
        className="absolute left-0 right-0 -top-[10%] w-full h-[120%] object-cover will-change-transform"
      />
      {particleEffect === 'mosquitoes' ? <Mosquito2D /> : <Pollen2D />}

      {/* Trust pill (rating + eyebrow) — top right */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="absolute top-[120px] lg:top-[140px] right-3 sm:right-5 lg:right-10 z-10"
      >
        <button
          type="button"
          onClick={onSecretTrustTap}
          aria-label={eyebrow || 'Trust badge'}
          data-testid="hero-secret-admin-trigger"
          className="flex items-center gap-2 px-2.5 sm:px-3.5 py-1.5 sm:py-2 rounded-full bg-white/95 backdrop-blur-md border border-black/5 shadow-[0_8px_22px_-12px_rgba(31,29,26,0.35)] text-[#1F1D1A] cursor-default select-none"
        >
          <span className="flex gap-0.5">
            {[0,1,2,3,4].map(i => <Star key={i} className="w-3 h-3 fill-[#E8B547] text-[#E8B547]" />)}
          </span>
          <span className="sm:hidden text-[11px] font-semibold tracking-wide">5.0</span>
          <span className="hidden sm:inline text-[11.5px] font-medium tracking-wide">{eyebrow}</span>
        </button>
      </motion.div>

      {/* Spar-Bundles — Mobile + Tablet (< lg, absolute top-left).
          Identical to the desktop circle, just smaller and anchored
          to the logo width. Idle: color-flip loop + pulse. Tap: scrolls
          to the bundles section (same as desktop click behaviour). */}
      {scrollHint && (
        <motion.div
          ref={mobilePillRef}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
          className="lg:hidden absolute top-[120px] left-6 sm:left-4 z-10"
        >
          <motion.button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onScrollHint();
            }}
            data-testid="hero-bundles-link-mobile"
            aria-label={scrollHint}
            className="group relative rounded-full backdrop-blur-sm flex flex-col items-center justify-center text-center shadow-[0_18px_44px_-18px_rgba(60,120,60,0.45)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FC242]/40 border-2 px-4 py-3 sm:px-5 sm:py-4"
            style={{
              width: 'var(--bundle-mobile-w)',
              height: 'var(--bundle-mobile-w)',
              // Black outline on all text inside, painted behind the fill,
              // so headlines stay legible over the sunny photo.
              paintOrder: 'stroke fill',
              WebkitTextStroke: '0.8px rgba(0,0,0,0.85)',
              textShadow: '0 1px 3px rgba(0,0,0,0.45)',
            }}
            initial={{
              backgroundColor: 'rgba(255,255,255,0.10)',
              borderColor:     'rgba(242,107,26,0.55)',
              color:           '#F26B1A',
              scale: 1,
            }}
            animate={{
              backgroundColor: [
                'rgba(255,255,255,0.10)',
                'rgba(255,255,255,0.10)',
                'rgba(127,194,66,0.10)',
                'rgba(127,194,66,0.10)',
                'rgba(255,255,255,0.10)',
              ],
              borderColor: [
                'rgba(242,107,26,0.55)',
                'rgba(242,107,26,0.55)',
                'rgba(255,255,255,0.75)',
                'rgba(255,255,255,0.75)',
                'rgba(242,107,26,0.55)',
              ],
              color: ['#F26B1A', '#F26B1A', '#FFFFFF', '#FFFFFF', '#F26B1A'],
              scale: [1, 1.03, 1, 1.03, 1],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              times: [0, 0.4, 0.5, 0.9, 1],
              ease: 'easeInOut',
            }}
          >
            {/* CSS variable for the mobile circle width = logo width.
                Mobile logo (size=16) ≈ 198px, sm logo (size=22) ≈ 272px. */}
            <style>{`
              [data-testid="hero-bundles-link-mobile"] {
                --bundle-mobile-w: 218px;
              }
              @media (min-width: 640px) {
                [data-testid="hero-bundles-link-mobile"] {
                  --bundle-mobile-w: 290px;
                }
              }
            `}</style>

            {/* Discount sticker — sits straddling the upper-right edge
                of the circle (slightly outside), rotated for a "tag" feel.
                Only renders when at least one bundle saves >0%. */}
            {maxSavePct > 0 && (
              <span
                aria-hidden="true"
                className="absolute z-[2] inline-flex items-center justify-center px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full bg-[#F26B1A] text-white text-[10.5px] sm:text-[12.5px] font-extrabold tracking-wide tabular-nums shadow-[0_6px_14px_-4px_rgba(0,0,0,0.45)] border border-white/80"
                style={{
                  top: '2%',
                  right: '-6%',
                  transform: 'rotate(10deg)',
                  paintOrder: 'normal',
                  WebkitTextStroke: '0',
                  textShadow: 'none',
                }}
              >
                −{maxSavePct}%
              </span>
            )}

            {/* Eyebrow */}
            <span className="inline-flex items-center gap-1.5 text-[10.5px] sm:text-[12px] tracking-[0.20em] sm:tracking-[0.28em] uppercase font-extrabold opacity-95 leading-tight">
              <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-full bg-current opacity-90" style={{ WebkitTextStroke: '0' }} />
              {scrollHint}
            </span>

            {/* Headline */}
            <div className="font-display text-[20px] sm:text-[26px] leading-tight mt-1 sm:mt-2 mb-1.5 sm:mb-3">
              {bundles.length} {bundles.length === 1 ? 'Paket' : 'Pakete'}
              <br />
              <span className="opacity-95 text-[10.5px] sm:text-[13px] font-bold not-italic uppercase tracking-[0.16em] sm:tracking-[0.18em]">
                zum Sparpreis
              </span>
            </div>

            {/* Bundle list — fully unfolded */}
            <ul className="w-full max-w-[170px] sm:max-w-[235px] space-y-1 sm:space-y-1.5 text-left">
              {bundles.map((b) => (
                <li
                  key={b.id}
                  className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-1.5 sm:gap-2 border-b border-current/20 pb-0.5 sm:pb-1.5 last:border-b-0"
                >
                  <span className="min-w-0 text-[10px] sm:text-[12px] font-bold leading-snug truncate">
                    {b.name}
                  </span>
                  {b.tagBadge && !b.soldOut ? (
                    <span
                      aria-hidden="true"
                      className="inline-flex items-center justify-center px-1 sm:px-1.5 py-[1px] rounded-full text-white text-[7px] sm:text-[8px] uppercase tracking-[0.06em] font-bold whitespace-nowrap shrink-0"
                      style={{ backgroundColor: b.tagBadge.color, paintOrder: 'normal', WebkitTextStroke: '0', textShadow: 'none' }}
                    >
                      {b.tagBadge.label}
                    </span>
                  ) : (
                    <span aria-hidden="true" />
                  )}
                  <span className="text-[10.5px] sm:text-[13px] font-extrabold tabular-nums whitespace-nowrap">{b.displayPrice}</span>
                </li>
              ))}
            </ul>

            {/* Footer hint */}
            <span className="mt-1.5 sm:mt-3 inline-flex items-center gap-1 text-[9px] sm:text-[11px] uppercase tracking-[0.16em] sm:tracking-[0.22em] font-extrabold opacity-90">
              alle ansehen
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="sm:w-3 sm:h-3" style={{ WebkitTextStroke: '0' }}>
                <path d="M6 9l6 6 6-6" />
              </svg>
            </span>
          </motion.button>
        </motion.div>
      )}

      {/* Gradient veil at the bottom — readability + soft handoff */}
      <div className="absolute inset-x-0 bottom-0 h-[55%] sm:h-[50%] pointer-events-none bg-gradient-to-t from-[#FAF8F2] via-[#FAF8F2]/85 to-transparent" />

      {/* Content — pushed to the very bottom of the section. The
          headline's lower edge meets the chevron line. */}
      <div className="relative z-[5] mt-auto w-full">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 pb-6 lg:pb-8">
          {/* Desktop: 2-col grid — H1 left, Spar-Bundles right.
              `items-end` puts the circle's bottom edge flush with the
              H1's last line ("Nicht die Insekten."). Below lg only the
              H1 renders here; the Spar-Bundles button lives in the
              absolute top-left pill above. */}
          <div className="lg:grid lg:grid-cols-12 lg:gap-8 lg:items-end">
            <h1
              aria-label={`${line1} ${line2}`}
              className="lg:col-span-8 font-display text-[#1F1D1A] text-[34px] sm:text-[56px] lg:text-[72px] xl:text-[84px] leading-[1.02] sm:leading-[0.98] max-w-6xl break-words"
            >
              <span className="block" aria-hidden="true">
                <TextEffect text={line1} delay={0.15} perCharDelay={0.022} />
              </span>
              <span className="block text-[#F26B1A]" aria-hidden="true">
                <TextEffect text={line2} delay={0.55} perCharDelay={0.022} />
              </span>
            </h1>

            {/* Spar-Bundles — circular CTA that fully reveals all bundles
                with prices instead of cycling them. Background is heavily
                transparent in both states so the tree + grass stay readable.
                Color-flip cycle stays as a subtle attention cue. */}
            {scrollHint && bannerRect && (
              <motion.div
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 1.45, ease: [0.22, 1, 0.36, 1] }}
                className="hidden lg:flex lg:col-span-4 justify-end self-start"
                style={{ marginTop: 'clamp(-250px, -28vh, -190px)' }}
              >
                <motion.button
                  type="button"
                  onClick={onScrollHint}
                  onHoverStart={() => setBundleHover(true)}
                  onHoverEnd={() => setBundleHover(false)}
                  data-testid="hero-bundles-link"
                  style={{ width: desktopBundleSize, height: desktopBundleSize, transformOrigin: 'center center' }}
                  className="group relative rounded-full backdrop-blur-sm inline-flex flex-col items-center justify-center text-center shadow-[0_24px_60px_-20px_rgba(60,120,60,0.45)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FC242]/40 border-2 px-8 py-8 xl:px-9 xl:py-9"
                  initial={{
                    backgroundColor: 'rgba(255,255,255,0.10)',
                    borderColor:     'rgba(242,107,26,0.55)',
                    color:           '#F26B1A',
                    scale: 1,
                  }}
                  animate={
                    bundleHover
                      ? {
                          backgroundColor: 'rgba(255,255,255,0.10)',
                          borderColor:     'rgba(242,107,26,0.65)',
                          color:           '#F26B1A',
                          scale:           1.2,
                        }
                      : {
                          backgroundColor: [
                            'rgba(255,255,255,0.10)',
                            'rgba(255,255,255,0.10)',
                            'rgba(127,194,66,0.10)',
                            'rgba(127,194,66,0.10)',
                            'rgba(255,255,255,0.10)',
                          ],
                          borderColor: [
                            'rgba(242,107,26,0.55)',
                            'rgba(242,107,26,0.55)',
                            'rgba(255,255,255,0.75)',
                            'rgba(255,255,255,0.75)',
                            'rgba(242,107,26,0.55)',
                          ],
                          color: ['#F26B1A', '#F26B1A', '#FFFFFF', '#FFFFFF', '#F26B1A'],
                          scale: [1, 1.02, 1, 1.02, 1],
                        }
                  }
                  transition={
                    bundleHover
                      ? { duration: 0.45, ease: [0.22, 1, 0.36, 1] }
                      : {
                          duration: 6,
                          repeat: Infinity,
                          times: [0, 0.4, 0.5, 0.9, 1],
                          ease: 'easeInOut',
                        }
                  }
                >
                  {/* Discount sticker — straddles the upper-right edge */}
                  {maxSavePct > 0 && (
                    <span
                      aria-hidden="true"
                      className="absolute z-[2] inline-flex items-center justify-center px-3 py-1 rounded-full bg-[#F26B1A] text-white text-[14px] font-extrabold tracking-wide tabular-nums shadow-[0_10px_22px_-6px_rgba(0,0,0,0.45)] border border-white/80"
                      style={{ top: '4%', right: '-4%', transform: 'rotate(10deg)' }}
                    >
                      −{maxSavePct}%
                    </span>
                  )}

                  {/* Eyebrow */}
                  <span className="inline-flex items-center gap-2 text-[11px] xl:text-[11.5px] tracking-[0.22em] uppercase font-bold opacity-90">
                    <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-full bg-current opacity-90" />
                    {scrollHint}
                  </span>

                  {/* Headline */}
                  <div className="font-display text-[23px] xl:text-[26px] leading-tight mt-2 mb-3">
                    {bundles.length} {bundles.length === 1 ? 'Paket' : 'Pakete'}
                    <br />
                    <span className="opacity-90 text-[12px] xl:text-[13px] font-medium not-italic uppercase tracking-[0.16em]">zum Sparpreis</span>
                  </div>

                  {/* Bundle list — fully unfolded */}
                  <ul className="w-full max-w-[300px] space-y-2 text-left">
                    {bundles.map((b) => (
                      <li
                        key={b.id}
                        className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-2 border-b border-current/15 pb-1.5 last:border-b-0"
                      >
                        <span className="min-w-0 text-[13px] xl:text-[14px] font-semibold leading-snug truncate">
                          {b.name}
                        </span>
                        {b.tagBadge && !b.soldOut ? (
                          <span
                            aria-hidden="true"
                            className="inline-flex items-center justify-center px-1.5 py-[2px] rounded-full text-white text-[8px] xl:text-[8.5px] uppercase tracking-[0.08em] font-bold whitespace-nowrap shrink-0"
                            style={{ backgroundColor: b.tagBadge.color }}
                          >
                            {b.tagBadge.label}
                          </span>
                        ) : (
                          <span aria-hidden="true" />
                        )}
                        <span className="text-[14px] xl:text-[15px] font-bold tabular-nums whitespace-nowrap">{b.displayPrice}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Footer hint */}
                  <span className="mt-3 inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.18em] font-semibold opacity-80">
                    alle ansehen
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </span>
                </motion.button>
              </motion.div>
            )}
          </div>

          {/* Sub-text + 3 Lead-CTAs.
              Mobile (< sm): CTAs render as compact round icon-only buttons.
              Tablet/Desktop (≥ sm): CTAs render as full pills with text. */}
          <div className="mt-5 lg:mt-7 grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-5 items-center">
            <motion.p
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.0, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-4 text-[#1F1D1A]/85 text-[14.5px] lg:text-[15.5px] leading-relaxed max-w-md"
            >
              {sub}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.0, delay: 1.25, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-8"
            >
              {/* Mobile (< sm): 3 round icon buttons anchored to the
                  Header's logo (left, x≈24) and hamburger right edge
                  (right, x≈W-16). Rendered in its OWN flex row so the
                  desktop Pills' Magnet-wrapper spans don't pollute the
                  flex flow. */}
              <div className="sm:hidden ml-1 -mr-1 flex flex-row justify-between items-center">
                {primary && (
                  <a
                    href={primary.href}
                    data-testid={`${primary.testId}-mobile`}
                    data-lead-source={primary.source}
                    aria-label={primary.label}
                    className="flex w-14 h-14 rounded-full bg-[#F26B1A] text-white items-center justify-center shadow-[0_10px_24px_-10px_rgba(242,107,26,0.6)] active:scale-95 transition-transform"
                  >
                    <Mail className="w-5 h-5" />
                  </a>
                )}
                {whatsapp && (
                  <a
                    href={whatsapp.href}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`${whatsapp.testId}-mobile`}
                    aria-label={whatsapp.label}
                    className="flex w-14 h-14 rounded-full bg-white text-[#1F1D1A] border border-[#1F1D1A]/15 items-center justify-center shadow-[0_8px_20px_-10px_rgba(31,29,26,0.35)] active:scale-95 transition-transform"
                  >
                    <MessageCircle className="w-5 h-5" />
                  </a>
                )}
                {phone && (
                  <a
                    href={phone.href}
                    data-testid={`${phone.testId}-mobile`}
                    aria-label={phone.label}
                    className="flex w-14 h-14 rounded-full bg-[#111] text-white items-center justify-center shadow-[0_8px_20px_-10px_rgba(31,29,26,0.55)] active:scale-95 transition-transform"
                  >
                    <Phone className="w-5 h-5" />
                  </a>
                )}
              </div>

              {/* Tablet+Desktop (≥ sm): 3 full pills */}
              <div className="hidden sm:flex flex-row gap-2.5 lg:justify-end whitespace-nowrap items-center">
                {primary && (
                  <Pill variant="leaf" href={primary.href} testId={primary.testId} source={primary.source}>
                    {primary.label}
                  </Pill>
                )}
                {whatsapp && (
                  <Pill variant="outline" icon="whatsapp" iconLeft href={whatsapp.href} testId={whatsapp.testId}
                    target="_blank" rel="noreferrer" magnetStrength={0.22}>
                    {whatsapp.label}
                  </Pill>
                )}
                {phone && (
                  <Pill
                    variant="dark"
                    icon="phone"
                    iconLeft
                    href={phone.href}
                    testId={phone.testId}
                    source="hero-phone"
                    magnetStrength={0.18}
                  >
                    {phone.number}
                  </Pill>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
